<script lang="ts">
  import { onDestroy } from "svelte";
  import { format, startOfToday } from "date-fns";
  import { zonedTimeToUtc } from "date-fns-tz";

  import type { GetOccupancySensorsQuery } from "../../frontendGatewaySdk";
  import { getAppContext } from "../../AppContext";
  import type { SensorData } from "./types";
  import { browserTimeZone } from "../../util/general";

  import TitledDisplay from "@cohesionib/biblioteca/lib/TitledDisplay";
  import Trend from "./OccupancySensorTrend.svelte";
  import SnapshotSection from "../../shared/SnapshotSection.svelte";

  let response: GetOccupancySensorsQuery | null = null;
  let error: string | null = null;

  const { feGatewaySdk, buildingCode, buildingCodeDemoList, buildingButlrSensorList, retoolButlrDashboardURL } = getAppContext();
  const buildingCodeLC = buildingCode.toLowerCase();
  const sensors = ["1st Floor Entrance South", "2nd Floor Entrance South"];
  const retoolURLwithBuildingCode = `${retoolButlrDashboardURL}${buildingButlrSensorList[buildingCodeLC]}?buildingCode=${buildingCodeLC}`;
  
  const refreshData = () =>
    feGatewaySdk
      .getOccupancySensors({
        buildingCode: buildingCodeDemoList.includes(buildingCodeLC) ? "cib" : buildingCodeLC,
        sensorIds: sensors,
        startOfDay: zonedTimeToUtc(startOfToday(), browserTimeZone),
        now: zonedTimeToUtc(new Date(), browserTimeZone),
      })
      .then((d: GetOccupancySensorsQuery) => {
        error = null;
        response = d;
      })
      .catch((e: string) => {
        error = e;
      });

  refreshData();
  const interval = setInterval(refreshData, 5000);
  onDestroy(() => clearInterval(interval));

  let dateTitle = format(new Date(), "MMMM do', 'yyyy'").replaceAll(".", "");

  const getTotalEntrants = (sensorData: SensorData) =>
    sensorData.reduce((prev, curr) => prev + (curr?.value || 0), 0) || 0;
</script>

<style>
  .layout {
    margin-top: 3.5em;
  }
  .snapshot-child {
    width: 11em;
    padding: 1.5em 2em 1.5em 2em;
  }
  #date-title {
    margin-top: -1.5em;
    font-size: 1.5em;
    color: #4e4e4e;
    font-weight: 400;
    padding-bottom: 1em;
  }
  @keyframes breathe {
    0% {
      opacity: 100%;
    }
    50% {
      opacity: 50%;
    }
    100% {
      opacity: 100%;
    }
  }
  #live {
    font-weight: 300;
    color: #68a691;
    animation: breathe 4s ease-in-out infinite;
  }
  #pipe {
    font-weight: 300;
  }
  .number {
    font-weight: 300;
    color: var(--theme-accent);
    font-size: 3em;
    line-height: min-content;
  }
  #trend-wrapper {
    padding-top: 2em;
  }
</style>

<div class="layout">
  {#if response === null && error === null}
    loading...
  {:else if response === null && error !== null}
    {error}
  {:else if Object.keys(buildingButlrSensorList).includes(buildingCodeLC)}
    <iframe src="{retoolURLwithBuildingCode}" style="width: 100%; height: 70vh; border: 0;"></iframe>
  {:else if response !== null}
    <div id="date-title">
      <span>{dateTitle}</span>
      <span id="pipe">{' | '}</span>
      <span id="live">live</span>
    </div>
    <SnapshotSection title="Snapshot">
      <div class="snapshot-child">
        <TitledDisplay
          title="Current Occupancy"
          subtitle="Total number of occupants who are currently in the building">
          <div class="number">
            {response.building?.anonymousTotalOccupancy || 0}
          </div>
        </TitledDisplay>
      </div>
      <div class="snapshot-child">
        <TitledDisplay
          title="Total Entrants"
          subtitle="Total number of entrants thus far today">
          <div class="number">
            {getTotalEntrants(response.building?.occupancyBySensor || [])}
          </div>
        </TitledDisplay>
      </div>
      <div class="snapshot-child">
        <TitledDisplay
          title="Occupancy Rate"
          subtitle="% of the {response.building?.capacity || 500} max occupancy that is currently in the building">
          <div class="number">
            {Math.round(((response.building?.anonymousTotalOccupancy || 0) / (response.building?.capacity || 500)) * 100)}%
          </div>
        </TitledDisplay>
      </div>
    </SnapshotSection>
    <div id="trend-wrapper">
      <Trend
        capacity={500}
        data={response.building?.occupancyBySensor || []} />
    </div>
  {/if}
</div>
